/**
 * The face box, from storage coordinates to CSS (ADR-0038, slice 04).
 *
 * The box is stored as ``[x1, y1, x2, y2]`` in 0–1 of the *original*, already
 * EXIF-corrected image (slice 01), so percentages of the rendered element are
 * enough: the highlight needs no rotation and no intrinsic image size.
 */

/** A box in 0–1, ordered and clipped to the image. */
export interface NormalizedBox {
  x: number
  y: number
  width: number
  height: number
}

/** Box as CSS percentages, ready for an absolutely positioned element. */
export interface BoxPercent {
  left: string
  top: string
  width: string
  height: string
}

/** *value* clipped to 0..1: a normalised coordinate on the image. */
export const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

/**
 * Order, clip and measure a stored box. Returns null when it is missing, not
 * four finite numbers, or collapsed to nothing after clipping.
 */
export function normalizeBox(box: readonly number[] | null | undefined): NormalizedBox | null {
  if (!box || box.length < 4 || box.slice(0, 4).some(value => !Number.isFinite(value))) return null
  const [x1, y1, x2, y2] = box.slice(0, 4).map(clamp01)
  const x = Math.min(x1, x2)
  const y = Math.min(y1, y2)
  const width = Math.abs(x2 - x1)
  const height = Math.abs(y2 - y1)
  return width > 0 && height > 0 ? { x, y, width, height } : null
}

/**
 * The box grown by *pad* of its own size on every side, still inside the image.
 * A little air makes the highlight read as "this face" instead of a tight mask.
 */
export function padBox(box: NormalizedBox, pad: number): NormalizedBox {
  const growX = box.width * pad
  const growY = box.height * pad
  const x = clamp01(box.x - growX)
  const y = clamp01(box.y - growY)
  return {
    x,
    y,
    width: Math.min(1 - x, box.width + 2 * growX),
    height: Math.min(1 - y, box.height + 2 * growY),
  }
}

const percent = (value: number) => `${(value * 100).toFixed(3)}%`

/** CSS percentages for a stored box, or null when there is nothing to draw. */
export function boxPercent(
  box: readonly number[] | null | undefined,
  { pad = 0 }: { pad?: number } = {}
): BoxPercent | null {
  const normalized = normalizeBox(box)
  if (!normalized) return null
  const final = pad > 0 ? padBox(normalized, pad) : normalized
  return {
    left: percent(final.x),
    top: percent(final.y),
    width: percent(final.width),
    height: percent(final.height),
  }
}

/** An element's rectangle in CSS pixels, relative to its offset parent. */
export interface DrawnRect {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Where `object-contain` actually paints the image inside its element box.
 *
 * The box above is a percentage of the *image*, but a contained image only
 * fills one axis of its element — the other is letterboxed. Percentages of the
 * element would therefore land beside the face on every photo whose aspect
 * ratio differs from its slot (a portrait photo in a landscape overlay).
 * Returns null while either size is still unknown: the image has not loaded,
 * or the element is not laid out yet.
 */
export function containRect(
  natural: { width: number; height: number },
  element: { width: number; height: number }
): DrawnRect | null {
  const { width: naturalWidth, height: naturalHeight } = natural
  const { width: elementWidth, height: elementHeight } = element
  if (![naturalWidth, naturalHeight, elementWidth, elementHeight].every(value => value > 0)) {
    return null
  }
  const scale = Math.min(elementWidth / naturalWidth, elementHeight / naturalHeight)
  const width = naturalWidth * scale
  const height = naturalHeight * scale
  return { left: (elementWidth - width) / 2, top: (elementHeight - height) / 2, width, height }
}

/** Two rects are the same drawing — lets a measurement skip a re-render. */
export function sameRect(a: DrawnRect | null, b: DrawnRect | null): boolean {
  if (!a || !b) return a === b
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height
}
